/* hs-eslint ignored failing-rules */
'use es6';
/*
 * Unified Layouts are layouts along with some metadata about content associated with the layout.
 */

import { omit, pick } from 'underscore';
import getQuickFetchRequestPromise from 'DesignData/api/getQuickFetchRequestPromise';
import * as layoutsClient from 'DesignData/api/Layouts';
import * as layoutBuffersClient from 'DesignData/api/LayoutBuffers';
import * as globalWidgetsClient from 'DesignData/api/GlobalWidgets';
import * as globalWidgetsCmv2Client from 'DesignData/api/GlobalWidgetsCmv2';
import * as customWidgetsClient from 'DesignData/api/CustomWidgets';
import * as customWidgetBuffersClient from 'DesignData/api/CustomWidgetBuffers';
import * as revisionsClient from 'DesignData/api/Revisions';
import * as defintionsClient from 'smart-content-core/api/Definitions';
import { isLegacyFormType, isImageType, isLegacyImageSliderType } from 'DesignData/helpers/widgets';
import { getSyncedFormWidgetParams, getSyncedImageWidgetParams, getSyncedImageSliderWidgetParams } from 'DesignData/lib/layoutTreeDataHelper';
import { createLayoutTreeFromNormalizedData, getNormalizedLayoutDataFromTree } from 'DesignData/lib/normalization/layouts';
import { createRowId } from 'DesignData/lib/normalization/layouts/identifiers';
import { findCustomWidget } from 'DesignData/lib/layouts/findCustomWidget';
import { getCustomWidgetsById, getCustomWidgetsByName } from 'DesignData/selectors/customWidgets';
import { getGlobalWidgetSummariesByName } from 'DesignData/selectors/globalWidgets';
import { getLayout, makeGetMeta, makeGetRequestStatus } from 'DesignData/selectors/layouts';
import { getBuiltinByType } from 'DesignData/selectors/layoutWidgets';
import { getCatalogWidgetForLayoutWidget, getHasSmartContent, getLayoutData, getWidget, getWidgetType } from 'DesignData/selectors/layoutData';
import { getGlobalGroupsDataByPath } from 'DesignData/selectors/globalGroups';
import { getDuplicateFileName } from 'DesignData/selectors/finder';
import { getSerializableLayout } from 'DesignData/selectors/layoutSerialization';
import { getIsModuleIdMigrationUngated } from 'DesignData/selectors/gates';
import { TOP, BOTTOM, LEFT, RIGHT } from 'DesignData/enums/OrientationTypes';
import { NOT_MODIFIED } from 'DesignData/enums/HttpStatusCodes';
import { LOCAL_COPY_OF_GLOBAL_GROUP_MISING_ID, RESTORE_MISSING_GLOBAL_GROUP_PATH, RESTORE_MISSING_GLOBAL_WIDGET_NAME, RESTORE_MISSING_CUSTOM_WIDGET_NAME } from 'DesignData/enums/LayoutWidgetErrorTypes';
import { GlobalGroup, GlobalWidgetSummary, Widget, CustomWidget } from 'DesignData/models/widgets';
import * as customWidgetFieldsHelpers from 'DesignData/helpers/customWidgetFieldsHelper';
import * as smartObjectHelper from 'DesignData/helpers/smartObjectHelper';
import { UNIFIED_LAYOUT_FETCH, LAYOUT_REVISION_FETCH, LAYOUT_BUFFER_SAVE, LAYOUT_PUBLISH, LAYOUT_CREATE_FROM_FILE, LAYOUT_DELETE, LAYOUT_MOVE_RENAME, LAYOUT_TOGGLE_EMAIL_TYPE, LAYOUT_PROPERTIES_EDIT, LAYOUT_WIDGET_ADD_WIDGET_BELOW, LAYOUT_WIDGET_SPLIT, LAYOUT_WIDGET_EDIT, LAYOUT_WIDGET_EDIT_CUSTOM_WIDGET, LAYOUT_WIDGET_EDIT_GLOBAL_WIDGET, LAYOUT_WIDGET_EDIT_PARAMS, LAYOUT_WIDGET_EDIT_LANGUAGE_OVERRIDES, LAYOUT_WIDGET_MAKE_GLOBAL, LAYOUT_WIDGET_MAKE_CMV2_GLOBAL, LAYOUT_WIDGET_MAKE_LOCAL_COPY_GLOBAL_WIDGET, LAYOUT_WIDGET_MAKE_LOCAL_COPY_CMV2_GLOBAL_WIDGET, LAYOUT_WIDGET_MAKE_LOCAL_COPY_GLOBAL_GROUP, LAYOUT_WIDGET_RESTORE_PARENT, LAYOUT_WIDGET_SWAP, LAYOUT_WIDGET_CLONE, LAYOUT_WIDGET_CSS_CLASSES_STRIP_DUPLICATES, LAYOUT_MULTI_WIDGET_EDIT_CSS_CLASSES, LAYOUT_CELL_EDIT_GLOBAL_GROUP, LAYOUT_CELL_MAKE_GLOBAL, LAYOUT_CELL_UNGROUP, LAYOUT_DND_UPDATE_CONTEXT_MONITOR, LAYOUT_DND_MOVE_WIDGET_WITHIN_ROW, LAYOUT_DND_MOVE_WIDGET_TO_NEW_ROW, LAYOUT_DND_MOVE_WIDGET_AND_CREATE_CELL, LAYOUT_DND_HORIZONTAL_RESIZE_WIDGETS, LAYOUT_DND_INSERT_SIDEBAR_WIDGET_IN_ROW, LAYOUT_DND_INSERT_SIDEBAR_WIDGET_AND_CREATE_CELL, LAYOUT_DND_MOVE_ROW, LAYOUT_DND_MOVE_WIDGET_AND_CREATE_ROW, LAYOUT_DND_MOVE_SIDEBAR_WIDGET_AND_CREATE_ROW, LAYOUT_EDIT_UNDO, LAYOUT_EDIT_REDO, LAYOUT_CLEAR_UNDO_HISTORY, LAYOUT_WIDGET_EDIT_SMART_RULES, LAYOUT_WIDGET_REMOVE_SMART_RULES, LAYOUT_WIDGET_ADD_SMART_RULE, LAYOUT_WIDGET_EDIT_SMART_RULE, LAYOUT_WIDGET_REMOVE_SMART_RULE, LAYOUT_WIDGET_EDIT_SMART_RULES_ORDER, LAYOUT_WIDGET_UPDATE_SMART_RULES, LAYOUT_REVERT, LAYOUT_DISCARD_BUFFER, LAYOUT_TO_CLONE_FROM_FETCH, LAYOUT_RENAME, LAYOUT_UPDATE_FOLDER, LAYOUT_TOGGLE_IS_AVAILABLE_FOR_NEW_CONTENT, LAYOUT_LOCALIZATION_DELETE_LANGUAGE, LAYOUT_LOCALIZATION_CHANGE_MASTER_LANGUAGE, LAYOUT_LOCALIZATION_OPT_IN, LAYOUT_LOCALIZATION_ADD_LANGUAGE } from 'DesignData/actions/ActionTypes';
import { toggleRenameAsset } from 'DesignData/actions/finder/Options';
import { revealInFinder } from 'DesignData/actions/finder/Selections';
export { hardDeleteWidgets, deleteWidget, deleteMultipleWidgets, deleteSelectedWidgets } from 'DesignData/actions/unifiedLayouts/WidgetDeletion';
const getRequestStatus = makeGetRequestStatus();
const getMeta = makeGetMeta();
/*
 * Fetch layout by id.
 *
 * GET designmanager/v1/layouts/:layoutId/buffer
 *
 * @param {(Number|String)} layoutId
 */

export function fetch(layoutId, {
  includeContentOverridden = true
} = {}) {
  const {
    request,
    receive,
    error
  } = fetch; // Important: This must return a promise. Used by optimistic checks
  // looking for content overridden.

  return (dispatch, getState) => {
    const state = getState();
    const currentLayout = getLayout(state, {
      layoutId
    });
    dispatch(request(layoutId));
    const query = {
      // Include isContentOverridden field in data, which
      // determines if there is downstream content edits.
      includeContentOverridden
    };

    const successHandler = layout => {
      dispatch(receive(layoutId, layout));
    };

    const errorHandler = __error => {
      if (__error.status && __error.status !== NOT_MODIFIED) {
        dispatch(error(layoutId, __error));
      }
    };

    if (!currentLayout) {
      const quickFetchPromise = getQuickFetchRequestPromise(`layoutId-${layoutId}`, () => layoutBuffersClient(layoutId, query));

      if (quickFetchPromise) {
        quickFetchPromise.then(successHandler).catch(errorHandler);
      }
    }

    if (currentLayout && currentLayout.updated) {
      // This query allows us to 304 if we already have latest from server.
      query.last_updated = currentLayout.updated;
    }

    return layoutBuffersClient.fetch(layoutId, query).then(successHandler).catch(errorHandler);
  };
}

fetch.request = function request(layoutId) {
  return {
    type: UNIFIED_LAYOUT_FETCH,
    layoutId
  };
};

fetch.receive = function receive(layoutId, layout) {
  return {
    type: UNIFIED_LAYOUT_FETCH,
    layoutId,
    layout
  };
};

fetch.error = function error(layoutId, err) {
  return {
    type: UNIFIED_LAYOUT_FETCH,
    layoutId,
    error: err
  };
};
/**
 * Fetch layout to clone from by id.
 *
 * GET designmanager/v1/layouts/unified/:layoutId
 */


export function fetchLayoutToCloneFrom(layoutId) {
  const {
    request,
    receive,
    error
  } = fetchLayoutToCloneFrom;
  return dispatch => {
    dispatch(request(layoutId));
    return layoutsClient.fetch(layoutId).then(layout => {
      dispatch(receive(layoutId, layout));
    }).catch(__error => {
      dispatch(error(layoutId, __error));
    });
  };
}

fetchLayoutToCloneFrom.request = layoutId => {
  return {
    type: LAYOUT_TO_CLONE_FROM_FETCH,
    layoutId
  };
};

fetchLayoutToCloneFrom.receive = (layoutId, layout) => {
  return {
    type: LAYOUT_TO_CLONE_FROM_FETCH,
    layoutId,
    layout
  };
};

fetchLayoutToCloneFrom.error = (layoutId, error) => {
  return {
    type: LAYOUT_TO_CLONE_FROM_FETCH,
    layoutId,
    error
  };
};
/**
 * Fetch layout revision by id.
 *
 */


export function fetchRevision(layoutId, revisionId) {
  const {
    request,
    receive,
    error
  } = fetchRevision;
  return (dispatch, getState) => {
    const state = getState();
    const revisionLayout = getLayout(state, {
      layoutId,
      revisionId
    });

    if (revisionLayout) {
      // Revisions will not change, no need to download again.
      return;
    }

    dispatch(request(layoutId));
    revisionsClient.fetchLayoutRevision(layoutId, revisionId).then(layout => {
      dispatch(receive(layoutId, revisionId, layout));
    }).catch(__error => {
      dispatch(error(layoutId, revisionId, __error));
    });
  };
}

fetchRevision.request = function request(layoutId, revisionId) {
  return {
    type: LAYOUT_REVISION_FETCH,
    layoutId,
    revisionId
  };
};

fetchRevision.receive = function receive(layoutId, revisionId, layout) {
  return {
    type: LAYOUT_REVISION_FETCH,
    layoutId,
    revisionId,
    layout
  };
};

fetchRevision.error = function error(layoutId, revisionId, err) {
  return {
    type: LAYOUT_REVISION_FETCH,
    layoutId,
    revisionId,
    error: err
  };
};

export function createFromFile(layout) {
  return {
    type: LAYOUT_CREATE_FROM_FILE,
    layout
  };
}
/*
 * PUT designmanager/v1/layouts/:id/buffer
 */

export function save(layoutId) {
  const {
    request,
    receive,
    error
  } = save;
  return (dispatch, getState) => {
    const state = getState();
    const {
      isSaving,
      isPublishing
    } = getRequestStatus(state, {
      layoutId
    });

    if (isSaving || isPublishing) {
      return null;
    }

    const layoutObject = getSerializableLayout(state, {
      layoutId
    });
    dispatch(request(layoutId));
    return layoutBuffersClient.save(layoutId, layoutObject).then(__layout => {
      dispatch(receive(layoutId, __layout));
    }).catch(__error => {
      dispatch(error(layoutId, __error));
    });
  };
}

save.request = function request(layoutId) {
  return {
    type: LAYOUT_BUFFER_SAVE,
    layoutId
  };
};

save.receive = function receive(layoutId, layout) {
  return {
    type: LAYOUT_BUFFER_SAVE,
    layoutId,
    layout
  };
};

save.error = function error(layoutId, err) {
  return {
    type: LAYOUT_BUFFER_SAVE,
    layoutId,
    error: err
  };
};
/*
 *  PUT designmanager/v1/layouts/:id
 */


export function publish(layoutId) {
  const {
    request,
    receive,
    error
  } = publish;
  return (dispatch, getState) => {
    const state = getState();
    const layoutObject = getSerializableLayout(state, {
      layoutId
    });
    dispatch(request(layoutId));
    return layoutsClient.publish(layoutId, layoutObject).then(__layout => {
      dispatch(receive(layoutId, __layout));
    }).catch(__error => {
      dispatch(error(layoutId, __error));
    });
  };
}

publish.request = function request(layoutId) {
  return {
    type: LAYOUT_PUBLISH,
    layoutId
  };
};

publish.receive = function receive(layoutId, layout) {
  return {
    type: LAYOUT_PUBLISH,
    layoutId,
    layout
  };
};

publish.error = function error(layoutId, err) {
  return {
    type: LAYOUT_PUBLISH,
    layoutId,
    error: err
  };
};
/**
 * Finder sync middleware action
 * @param {(number, number[])} a single layout id or array of layout ids to delete.
 */


export function deleteLayouts(layoutIds, deleted = true) {
  return {
    type: LAYOUT_DELETE,
    layoutId: layoutIds,
    deleted
  };
}
/**
 * Finder sync middleware action
 */

export function moveOrRename(layoutId, layout) {
  return {
    type: LAYOUT_MOVE_RENAME,
    layoutId,
    layout
  };
}
/*
 * Toggle an email's type between basic/responsive.
 */

export function toggleEmailResponsive(layoutId) {
  return (dispatch, getState) => {
    const state = getState();
    const layout = getLayout(state, {
      layoutId
    });
    const generator = layout.generator === 'responsive_email' ? null : 'responsive_email';
    dispatch({
      type: LAYOUT_TOGGLE_EMAIL_TYPE,
      layoutId,
      generator
    });
  };
}
/*
 * Toggle isAvailableForNewContent property of a layout
 */

export function toggleIsAvailableForNewContent(layoutId) {
  return {
    type: LAYOUT_TOGGLE_IS_AVAILABLE_FOR_NEW_CONTENT,
    layoutId
  };
}
/**
 * Edit immediate fields of a layout.
 *  NOT layoutData
 *  NOT nested data - See editWidgetParams for an example of something similar for that.
 *
 * @example
 *   // Lock module
 *   editLayoutProperties(12345, {customHead: '<style>...</style>', enableDomainStylesheets: true});
 *
 * @param {String|Number} layoutId   Id of layout.
 * @param {Object}        edits      Object of fields to merge in the layout's state.
 */

export function editLayoutProperties(layoutId, edits = {}) {
  return {
    type: LAYOUT_PROPERTIES_EDIT,
    layoutId,
    edits
  };
}
/**
 * PUT designmanager/v1/layouts/layoutId
 * @param {Number} layoutId
 * @param {String} label
 *
 * Writes name directly to layout instead of layout buffer
 */

export function renameLayout(layoutId, label) {
  const {
    request,
    receive,
    error
  } = renameLayout;
  return dispatch => {
    dispatch(request(layoutId));
    layoutsClient.save(layoutId, {
      label
    }).then(layout => {
      dispatch(receive(layoutId, layout.label));
    }).catch(__error => {
      dispatch(error(layoutId, __error));
    });
  };
}

renameLayout.request = layoutId => {
  return {
    type: LAYOUT_RENAME,
    layoutId
  };
};

renameLayout.receive = (layoutId, label) => {
  return {
    type: LAYOUT_RENAME,
    layoutId,
    label
  };
};

renameLayout.error = (layoutId, error) => {
  return {
    type: LAYOUT_RENAME,
    layoutId,
    error
  };
};
/*
 * Widget level actions.
 */

/**
 * Add Widget Below
 *
 * @param {String|Number} layoutId  Id of layout with widget.
 * @param {String}        widgetId  Id of widget to add another widget below.
 * @param {String}        rowId     Id of row in which widget exists.
 */


export function addWidgetBelow(layoutId, widgetId, rowId) {
  return (dispatch, getState) => {
    const state = getState();
    const cell = getBuiltinByType(state, 'cell');
    const richText = getBuiltinByType(state, 'rich_text');
    dispatch({
      type: LAYOUT_WIDGET_ADD_WIDGET_BELOW,
      layoutId,
      widgetId,
      rowId,
      newCell: cell.generateNewLayoutWidgetObject(),
      newWidget: richText.generateNewLayoutWidgetObject()
    });
  };
}
/**
 * Clone widget
 *
 * @param {String|Number} layoutId        Id of layout with widget.
 * @param {String}        widgetId        Id of widget to add another widget below.
 * @param {String}        rowId           Id of row in which widget exists.
 * @param {String}        parentId        Id of widget's parent widget.
 */

export function cloneWidget(layoutId, widgetId, rowId, parentId) {
  return (dispatch, getState) => {
    const state = getState();
    const layoutData = getLayoutData(state, {
      layoutId
    });

    if (!(layoutData && layoutData.widgets[widgetId] && layoutData.widgets[parentId])) {
      return;
    } // Copy widget to a new normalized dataset.


    const tree = createLayoutTreeFromNormalizedData(layoutData.widgets, layoutData.rows, widgetId);

    const nodeModifier = widget => {
      // Create new id/name for each widget.
      widget.id = Widget.generateWidgetName();

      if (widget.type !== 'global_widget') {
        widget.name = widget.id;
      }
    };

    const {
      widgets: cloneWidgets,
      rows: cloneRows,
      rootWidgetId: cloneWidgetId
    } = getNormalizedLayoutDataFromTree(tree, {
      nodeModifier
    });
    const cloneRowId = createRowId();
    dispatch({
      type: LAYOUT_WIDGET_CLONE,
      layoutId,
      widgetId,
      parentId,
      rowId,
      cloneWidgetId,
      cloneWidgets,
      cloneRowId,
      cloneRows
    });
  };
}
/**
 * Split a layout widget via split command.
 *
 * @param {String|Number} layoutId
 * @param {String}        rowId     Id of row in `layoutData[layoutId].rows` collection.
 * @param {String}        widgetId  Id of widget to split.
 * @param {String}        parentId  Id of widget's parent.
 */

export function splitWidget(layoutId, rowId, widgetId, parentId) {
  return (dispatch, getState) => {
    const state = getState();
    const widgetToBeSplit = getWidget(state, {
      layoutId,
      widgetId
    });

    if (widgetToBeSplit.w <= 1) {
      return;
    }

    const widgetCatalogModel = getCatalogWidgetForLayoutWidget(state, {
      layoutId,
      widgetId
    });
    const {
      isEmail
    } = getMeta(state, {
      layoutId
    });
    const newWidget = widgetCatalogModel.generateNewLayoutWidgetObject({
      isEmail
    });
    dispatch({
      type: LAYOUT_WIDGET_SPLIT,
      layoutId,
      rowId,
      widgetId,
      parentId,
      newWidget
    });
  };
}
/**
 * Edit widget value properties.
 *
 * - Not `rows`
 * - Not `params`
 *
 * @example
 *   // Change `label` field
 *   editWidget('widget_123', {label: 'New Label'});
 *   // Change `cssClass` field
 *   editWidget('widget_123', {cssClass: 'my-css-class'});
 *
 * @param {String|Number} layoutId   Id of layout with widget.
 * @param {String}        widgetId   Id of widget to update.
 * @param {Object}        edits      Object of fields to merge in the widget model.
 */

export function editWidget(layoutId, widgetId, edits = {}) {
  return {
    type: LAYOUT_WIDGET_EDIT,
    layoutId,
    widgetId,
    edits
  };
}
/**
 * Edit values in a widget's `params` object.
 *
 * @example
 *   // Lock module
 *   editWidget('widget_123', {overrideable: false});
 *
 * @param {String|Number} layoutId   Id of layout with widget.
 * @param {String}        widgetId   Id of widget to update.
 * @param {Object}        edits      Object of fields to merge in the widget's params object.
 */

export function editWidgetParams(layoutId, widgetId, _edits = {}, options = {}) {
  const {
    silent = false
  } = options;
  return (dispatch, getState) => {
    const state = getState();
    const widget = getWidget(state, {
      layoutId,
      widgetId
    });
    let edits = _edits;

    if (isImageType(widget)) {
      edits = getSyncedImageWidgetParams(widget, edits);
    } else if (isLegacyImageSliderType(widget.type)) {
      edits = getSyncedImageSliderWidgetParams(edits);
    } else if (isLegacyFormType(widget.type)) {
      edits = getSyncedFormWidgetParams(edits);
    }

    return dispatch({
      type: LAYOUT_WIDGET_EDIT_PARAMS,
      layoutId,
      widgetId,
      edits,
      silent
    });
  };
}
export function editWidgetLanguageOverrides(layoutId, widgetId, langCode, edits = {}) {
  return {
    type: LAYOUT_WIDGET_EDIT_LANGUAGE_OVERRIDES,
    layoutId,
    widgetId,
    langCode,
    edits
  };
}
/**
 * Edit values in a widget's `params` object and publish immediately after.
 * Specifically for publishing when editing ace content in the layout editor
 *
 * @param {String|Number} layoutId   Id of layout with widget.
 * @param {String}        widgetId   Id of widget to update.
 * @param {Object}        edits      Object of fields to merge in the widget's params object.
 */

export function publishWithWidgetParams(layoutId, widgetId, edits) {
  return dispatch => {
    dispatch(editWidgetParams(layoutId, widgetId, edits, {
      silent: true
    }));
    dispatch(publish(layoutId));
  };
}
/**
 * Edit CSS classes of multiple widgets.
 *
 * @param {String|Number}   layoutId        Id of layout.
 * @param {Array.<String>}  widgetIds       Ids of widgets to edit.
 * @param {Object}          nextClassData   The new shared classData.
 * @param {Object}          prevClassData   The previous shared classData.
 */

export function editMultipleWidgetCssClasses(layoutId, widgetIds, nextClassData, prevClassData) {
  return {
    type: LAYOUT_MULTI_WIDGET_EDIT_CSS_CLASSES,
    layoutId,
    widgetIds,
    nextClassData,
    prevClassData
  };
}
/**
 * Strip duplicate css classes from widgets.
 *
 * @param {String|Number}   layoutId        Id of layout.
 * @param {Array.<String>}  widgetIds       Ids of widgets.
 */

export function stripCssClassDuplicates(layoutId, widgetIds) {
  return {
    type: LAYOUT_WIDGET_CSS_CLASSES_STRIP_DUPLICATES,
    layoutId,
    widgetIds
  };
}
/**
 * Swap widget.
 *
 * @param {String|Number}               layoutId  Id of layout with widget.
 * @param {String}                      widgetId  Id of widget to swap.
 * @param {DesignData/models/widgets/*} widget    Widget model for new type.
 */

export function swapWidget(layoutId, widgetId, widget) {
  return (dispatch, getState) => {
    const {
      isEmail
    } = getMeta(getState(), {
      layoutId
    });
    const newWidget = widget.generateNewLayoutWidgetObject({
      isEmail
    });
    dispatch({
      type: LAYOUT_WIDGET_SWAP,
      layoutId,
      widgetId,
      newWidget
    });
  };
}
/**
 * Ungroup a cell.
 *
 * @param {String|Number} layoutId         Id of layout with widget.
 * @param {String}        widgetId         Id of cell to ungroup.
 * @param {String}        rowId            Id of row in which cell exists.
 */

export function ungroupCell(layoutId, widgetId, rowId) {
  return {
    type: LAYOUT_CELL_UNGROUP,
    layoutId,
    widgetId,
    rowId
  };
}
/*********************************************************
 *
 * Drag-n-Drop
 *
 *********************************************************/

/**
 *
 *
 */

export function updateDragDropContextMonitor(isDragging, itemType, item) {
  return {
    type: LAYOUT_DND_UPDATE_CONTEXT_MONITOR,
    isDragging,
    itemType,
    item
  };
}
/**
 * Move a widget to another widgets TOP/BOTTOM/LEFT/RIGHT drop target.
 *
 * @param {String|Number}                     layoutId        Id of layout.
 * @param {String}                            widgetId        Widget id of the widget dragged and dropped.
 * @param {String}                            rowId           Row id of the widget dragged and dropped.
 * @param {String}                            parentId        Widget id of cell containing the widget dragged and dropped.
 * @param {String}                            targetWidgetId  Widget id of the widget dropped on.
 * @param {String}                            targetRowId     Row id of the widget dropped on.
 * @param {String}                            targetParentId  Parent id of the widget dropped on.
 * @param {DesignData/enums/OrientationTypes} orientation     Relative location of drop on target widget.
 */

export function dropWidgetOnWidget(layoutId, widgetId, rowId, parentId, targetWidgetId, targetRowId, targetParentId, orientation) {
  const {
    reorder,
    moveToNewRow,
    moveAndWrapWidgetsInCell
  } = dropWidgetOnWidget;
  return (dispatch, getState) => {
    switch (orientation) {
      case LEFT:
      case RIGHT:
        {
          if (rowId === targetRowId) {
            dispatch(reorder(layoutId, widgetId, rowId, targetParentId, targetWidgetId, orientation));
          } else {
            dispatch(moveToNewRow(layoutId, widgetId, rowId, parentId, targetWidgetId, targetRowId, targetParentId, orientation));
          }

          break;
        }

      case TOP:
      case BOTTOM:
        {
          const state = getState();
          const cell = getBuiltinByType(state, 'cell');
          const newCell = cell.generateNewLayoutWidgetObject();
          dispatch(moveAndWrapWidgetsInCell(layoutId, widgetId, rowId, parentId, targetWidgetId, targetRowId, targetParentId, orientation, newCell));
          break;
        }

      default:
        break;
    }
  };
}

dropWidgetOnWidget.reorder = function reorder(layoutId, widgetId, rowId, parentId, targetWidgetId, orientation) {
  return {
    type: LAYOUT_DND_MOVE_WIDGET_WITHIN_ROW,
    layoutId,
    widgetId,
    rowId,
    parentId,
    targetWidgetId,
    orientation
  };
};

dropWidgetOnWidget.moveToNewRow = function moveToNewRow(layoutId, widgetId, rowId, parentId, targetWidgetId, targetRowId, targetParentId, orientation) {
  return {
    type: LAYOUT_DND_MOVE_WIDGET_TO_NEW_ROW,
    layoutId,
    widgetId,
    rowId,
    parentId,
    targetWidgetId,
    targetRowId,
    targetParentId,
    orientation
  };
};

dropWidgetOnWidget.moveAndWrapWidgetsInCell = function moveAndWrapWidgetsInCell(layoutId, widgetId, rowId, parentId, targetWidgetId, targetRowId, targetParentId, orientation, newCell) {
  return {
    type: LAYOUT_DND_MOVE_WIDGET_AND_CREATE_CELL,
    layoutId,
    widgetId,
    rowId,
    parentId,
    targetWidgetId,
    targetRowId,
    targetParentId,
    orientation,
    newCell
  };
};
/**
 * Move a widget to another widgets TOP/BOTTOM/LEFT/RIGHT drop target.
 *
 * @param {String|Number}                     layoutId        Id of layout.
 * @param {DesignData/models/widgets/Widget}  widget          Widget object representation from sidebar.
 * @param {String}                            targetWidgetId  Widget id of the widget dropped upon.
 * @param {String}                            targetRowId     Row id of the widget dropped upon.
 * @param {String}                            targetParentId  Widget id of the widget dropped upon's parent.
 * @param {DesignData/enums/OrientationTypes} orientation     Relative location of drop on target widget.
 */


export function dropSidebarWidgetOnWidget(layoutId, widget, targetWidgetId, targetRowId, targetParentId, orientation) {
  const {
    insert,
    insertAndWrapWidgetsInCell
  } = dropSidebarWidgetOnWidget;
  return (dispatch, getState) => {
    const state = getState();
    const {
      isEmail
    } = getMeta(state, {
      layoutId
    });
    const newWidget = widget.generateNewLayoutWidgetObject({
      isEmail
    });

    switch (orientation) {
      case LEFT:
      case RIGHT:
        {
          dispatch(insert(layoutId, newWidget, targetWidgetId, targetRowId, targetParentId, orientation));
          break;
        }

      case TOP:
      case BOTTOM:
        {
          const cell = getBuiltinByType(state, 'cell');
          const newCell = cell.generateNewLayoutWidgetObject();
          dispatch(insertAndWrapWidgetsInCell(layoutId, newWidget, targetWidgetId, targetRowId, targetParentId, orientation, newCell));
          break;
        }

      default:
        break;
    }
  };
}

dropSidebarWidgetOnWidget.insert = function insert(layoutId, widget, targetWidgetId, targetRowId, targetParentId, orientation) {
  return {
    type: LAYOUT_DND_INSERT_SIDEBAR_WIDGET_IN_ROW,
    layoutId,
    widget,
    targetWidgetId,
    targetRowId,
    targetParentId,
    orientation
  };
};

dropSidebarWidgetOnWidget.insertAndWrapWidgetsInCell = function insertAndWrapWidgetsInCell(layoutId, widget, targetWidgetId, targetRowId, targetParentId, orientation, newCell) {
  return {
    type: LAYOUT_DND_INSERT_SIDEBAR_WIDGET_AND_CREATE_CELL,
    layoutId,
    widget,
    targetWidgetId,
    targetRowId,
    targetParentId,
    orientation,
    newCell
  };
};
/**
 * Move a Row to a row target.
 *
 * @param {String} layoutId             Id of layout.
 * @param {String} sourceParentId       Widget id of the cell containing the source row.
 * @param {String} sourceRowId          Row id of the row being dragged and dropped.
 * @param {String} targetParentId       Widget id of the cell containing the row drop target.
 * @param {(String|Null)} targetRowId   Optional. Row id of the row to use as an insertion index. If not defined an append will be performed.
 */


export function dropRowOnRowTarget(layoutId, sourceParentId, sourceRowId, targetParentId, targetRowId = null) {
  return {
    type: LAYOUT_DND_MOVE_ROW,
    layoutId,
    sourceParentId,
    sourceRowId,
    targetParentId,
    targetRowId
  };
}
/**
 * Move a widget to a row target.
 *
 * @param {String} layoutId             Id of layout.
 * @param {String} sourceParentId       Widget id of the cell containing the source row.
 * @param {String} sourceRowId          Row id of the row being dragged and dropped.
 * @param {String} sourceWidgetId       Widget id of the widget being dragged and dropped.
 * @param {String} targetParentId       Widget id of the cell containing the row drop target.
 * @param {(String|Null)} targetRowId   Optional. Row id of the row to use as an insertion index. If not defined an append will be performed.
 */

export function dropWidgetOnRowTarget(layoutId, sourceParentId, sourceRowId, sourceWidgetId, targetParentId, targetRowId = null) {
  return {
    type: LAYOUT_DND_MOVE_WIDGET_AND_CREATE_ROW,
    layoutId,
    sourceParentId,
    sourceRowId,
    sourceWidgetId,
    targetParentId,
    targetRowId
  };
}
/**
 * Move a sidebar widget to a row target.
 *
 * @param {String}                            layoutId        Id of layout.
 * @param {DesignData/models/widgets/Widget}  widget          Widget object representation from sidebar.
 * @param {String}                            targetParentId  Widget id of the cell containing the row drop target.
 * @param {(String|Null)}                     targetRowId     Optional. Row id of the row to use as an insertion index. If not defined an append will be performed.
 */

export function dropSidebarWidgetOnRowTarget(layoutId, widget, targetParentId, targetRowId = null) {
  return (dispatch, getState) => {
    const {
      isEmail
    } = getMeta(getState(), {
      layoutId
    });
    const newWidget = widget.generateNewLayoutWidgetObject({
      isEmail
    });
    dispatch({
      type: LAYOUT_DND_MOVE_SIDEBAR_WIDGET_AND_CREATE_ROW,
      layoutId,
      widget: newWidget,
      targetParentId,
      targetRowId
    });
  };
}
/**
 * Horizontally resize adjoining widgets.
 *
 * @param {Number}                                          layoutId        Id of layout.
 * @param {String}                                          leftWidgetId    Widget id of the left widget being resized.
 * @param {String}                                          rightWidgetId   Widget id of the right widget being resized.
 * @param {(OrientationTypes.LEFT|OrientationTypes.RIGHT)}  orientation     Left or right.
 * @param {Number}                                          increment       Size of resize (Bootstrap grid span*)
 */

export function resizeWidgetsHorizontally(layoutId, leftWidgetId, rightWidgetId, orientation, increment) {
  return {
    type: LAYOUT_DND_HORIZONTAL_RESIZE_WIDGETS,
    layoutId,
    leftWidgetId,
    rightWidgetId,
    orientation,
    increment
  };
}
/*********************************************************
 *
 * Undo / Redo
 *
 *********************************************************/

/**
 * @param {String} layoutId   Id of layout.
 */

export function undoLayoutEdit(layoutId) {
  return {
    type: LAYOUT_EDIT_UNDO,
    layoutId
  };
}
/**
 * @param {String} layoutId   Id of layout.
 */

export function redoLayoutEdit(layoutId) {
  return {
    type: LAYOUT_EDIT_REDO,
    layoutId
  };
}
/**
 * @param {String} layoutId   Id of layout.
 */

export function clearUndoHistory(layoutId) {
  return {
    type: LAYOUT_CLEAR_UNDO_HISTORY,
    layoutId
  };
}
/*
 * Globalize Widget
 *
 * @param {String|Number} layoutId     Id of layout with widget.
 * @param {String}        widgetId     Id of widget to globalize.
 * @param {String}        widgetLabel  Label of global widget.
 */

export function makeWidgetGlobal(layoutId, widgetId, widgetLabel) {
  const {
    request,
    receive,
    swap,
    error
  } = makeWidgetGlobal;
  return (dispatch, getState) => {
    const state = getState();
    const targetWidget = getWidget(state, {
      layoutId,
      widgetId
    });

    if (targetWidget) {
      const requestData = {
        name: widgetLabel.replace(/\s/g, '_').replace(/[^a-zA-Z0-9_]/g, '').toLowerCase(),
        type: targetWidget.type,
        label: widgetLabel,
        body: omit(targetWidget.params, 'overrideable', Widget.DEFAULT_CSS_FIELDS)
      };
      dispatch(request(layoutId, widgetId, requestData));
      globalWidgetsClient.create(requestData).then(globalWidget => {
        dispatch(receive(layoutId, widgetId, globalWidget));
        const widget = new GlobalWidgetSummary(globalWidget);
        const newWidget = widget.generateNewLayoutWidgetObject();
        newWidget.params = Object.assign({}, newWidget.params, {}, pick(targetWidget.params, Widget.DEFAULT_CSS_FIELDS));
        dispatch(swap(layoutId, widgetId, newWidget));
      }).catch(__error => {
        dispatch(error(layoutId, widgetId, __error));
      });
      return;
    }

    dispatch(error(widgetId, new Error('Invalid request to make global module')));
  };
}

makeWidgetGlobal.request = (layoutId, widgetId, data) => {
  return {
    type: LAYOUT_WIDGET_MAKE_GLOBAL,
    layoutId,
    widgetId,
    data
  };
};

makeWidgetGlobal.receive = (layoutId, widgetId, globalWidget) => {
  return {
    type: LAYOUT_WIDGET_MAKE_GLOBAL,
    layoutId,
    widgetId,
    globalWidget
  };
};

makeWidgetGlobal.swap = (layoutId, widgetId, newWidget) => {
  return {
    type: LAYOUT_WIDGET_SWAP,
    layoutId,
    widgetId,
    newWidget
  };
};

makeWidgetGlobal.error = (layoutId, widgetId, error) => {
  return {
    type: LAYOUT_WIDGET_MAKE_GLOBAL,
    layoutId,
    widgetId,
    error
  };
};
/*
 * Globalize CMv2 Widget
 *
 * @param {String|Number} layoutId     Id of layout with widget.
 * @param {String}        widgetId     Id of widget to globalize.
 * @param {String}        widgetLabel  Label of global widget.
 */


export function makeCmv2WidgetGlobal(layoutId, widgetId, widgetLabel) {
  const {
    request,
    receive,
    swap,
    error
  } = makeCmv2WidgetGlobal;
  return (dispatch, getState) => {
    const state = getState();
    const targetWidget = getWidget(state, {
      layoutId,
      widgetId
    });

    if (targetWidget) {
      const requestData = {
        name: widgetLabel,
        type: targetWidget.type,
        label: widgetLabel,
        body: omit(targetWidget.params, 'overrideable', Widget.DEFAULT_CSS_FIELDS)
      };
      const catalogWidget = getCatalogWidgetForLayoutWidget(state, {
        layoutId,
        widgetId
      });

      if (catalogWidget && catalogWidget.fields) {
        let normalizedFields = customWidgetFieldsHelpers.getNormalizedFields(catalogWidget.fields, true);
        normalizedFields = customWidgetFieldsHelpers.editGlobalContent(normalizedFields, targetWidget.params);
        requestData.fields = Object.values(normalizedFields.fields || {});
      }

      dispatch(request(layoutId, widgetId, requestData));
      globalWidgetsCmv2Client.create(requestData).then(globalCustomWidget => {
        dispatch(receive(layoutId, widgetId, globalCustomWidget));
        const widget = new CustomWidget(globalCustomWidget);
        const newWidget = widget.generateNewLayoutWidgetObject();
        newWidget.params = Object.assign({}, newWidget.params, {}, pick(targetWidget.params, Widget.DEFAULT_CSS_FIELDS));
        dispatch(swap(layoutId, widgetId, newWidget));
      }).catch(__error => {
        dispatch(error(layoutId, widgetId, __error, requestData));
      });
      return;
    }

    dispatch(error(widgetId, new Error('Invalid request to make global module')));
  };
}

makeCmv2WidgetGlobal.request = (layoutId, widgetId, data) => {
  return {
    type: LAYOUT_WIDGET_MAKE_CMV2_GLOBAL,
    layoutId,
    widgetId,
    data
  };
};

makeCmv2WidgetGlobal.receive = (layoutId, widgetId, globalCustomWidget) => {
  return {
    type: LAYOUT_WIDGET_MAKE_CMV2_GLOBAL,
    layoutId,
    widgetId,
    customWidget: globalCustomWidget
  };
};

makeCmv2WidgetGlobal.swap = (layoutId, widgetId, newWidget) => {
  return {
    type: LAYOUT_WIDGET_SWAP,
    layoutId,
    widgetId,
    newWidget
  };
};

makeCmv2WidgetGlobal.error = (layoutId, widgetId, error, data) => {
  return {
    type: LAYOUT_WIDGET_MAKE_CMV2_GLOBAL,
    layoutId,
    widgetId,
    error,
    data
  };
};
/**
 * Edit a global widget.
 *
 * @param {String|Number} layoutId  Id of layout with widget.
 * @param {String} widgetId         Id of widget to edit.
 */


export function editGlobalWidget(layoutId, widgetId) {
  return (dispatch, getState) => {
    const state = getState();
    const targetWidget = getWidget(state, {
      layoutId,
      widgetId
    });

    if (targetWidget) {
      const widgetName = targetWidget.name.toLowerCase();
      const globalWidget = getGlobalWidgetSummariesByName(state)[widgetName];

      if (globalWidget) {
        dispatch({
          type: LAYOUT_WIDGET_EDIT_GLOBAL_WIDGET,
          globalWidget
        });
      } else {
        dispatch({
          type: LAYOUT_WIDGET_EDIT_GLOBAL_WIDGET,
          error: new ReferenceError(`Unable to find global widget with name '${targetWidget.name}'`),
          name: widgetName
        });
      }
    }
  };
}
/**
 * Make local copy of global module
 *
 * @param {String|Number} layoutId  Id of layout with widget.
 * @param {String} widgetId         Id of widget to make local copy of.
 */

export function makeLocalCopyOfGlobalWidget(layoutId, widgetId) {
  const {
    request,
    swap,
    error
  } = makeLocalCopyOfGlobalWidget;
  return (dispatch, getState) => {
    const state = getState();
    const propsForSelectors = {
      layoutId,
      widgetId
    };
    const targetWidget = getWidget(state, propsForSelectors);

    if (targetWidget) {
      dispatch(request(layoutId, widgetId));
      const widgetName = targetWidget.name.toLowerCase();
      const globalWidget = getGlobalWidgetSummariesByName(state)[widgetName];
      const globalWidgetId = globalWidget ? globalWidget.id : undefined;

      if (globalWidgetId) {
        let requests = [globalWidgetsClient.fetch(globalWidgetId)];

        if (getHasSmartContent(state, propsForSelectors)) {
          const {
            definition_id: definitionId
          } = globalWidget.body;
          requests = requests.concat(defintionsClient.clone(definitionId, {
            targetId: globalWidgetId
          }));
        }

        Promise.all(requests).then(([globalWidgetData, clonedDefinition]) => {
          const widget = new Widget(pick(globalWidgetData, 'label', 'type'));
          let newWidget = widget.generateNewLayoutWidgetObject();
          newWidget.params = Object.assign({}, newWidget.params, {}, pick(targetWidget.params, Widget.DEFAULT_CSS_FIELDS), {}, omit(globalWidgetData.body, 'id'));

          if (clonedDefinition) {
            newWidget = smartObjectHelper.updateWidgetWithClonedDefinition(newWidget, clonedDefinition);
          }

          dispatch(swap(layoutId, widgetId, newWidget));
        }).catch(_error => {
          dispatch(error(layoutId, widgetId, _error));
        });
        return;
      }

      dispatch(error(layoutId, widgetId, new ReferenceError('Unable to find global module')));
      return;
    }

    dispatch(error(layoutId, widgetId, new Error('Invalid request to make local copy of global module')));
  };
}

makeLocalCopyOfGlobalWidget.request = (layoutId, widgetId) => {
  return {
    type: LAYOUT_WIDGET_MAKE_LOCAL_COPY_GLOBAL_WIDGET,
    layoutId,
    widgetId
  };
};

makeLocalCopyOfGlobalWidget.swap = (layoutId, widgetId, newWidget) => {
  return {
    type: LAYOUT_WIDGET_SWAP,
    layoutId,
    widgetId,
    newWidget
  };
};

makeLocalCopyOfGlobalWidget.error = (layoutId, widgetId, error) => {
  return {
    type: LAYOUT_WIDGET_MAKE_LOCAL_COPY_GLOBAL_WIDGET,
    layoutId,
    widgetId,
    error
  };
};
/**
 * Make clone of CMv2 global module and swap widget over to clone
 *
 * @param {String|Number} layoutId  Id of layout with widget.
 * @param {String} widgetId         Id of widget to make local copy of.
 */


export function makeLocalCopyOfCmv2GlobalWidget(layoutId, widgetId) {
  const {
    request,
    receive,
    error
  } = makeLocalCopyOfCmv2GlobalWidget;
  return (dispatch, getState) => {
    const state = getState();
    const targetWidget = getWidget(state, {
      layoutId,
      widgetId
    });

    if (targetWidget) {
      const customWidgetsData = getCustomWidgetsById(state);
      const customWidget = findCustomWidget(targetWidget, customWidgetsData);
      const customWidgetId = customWidget && customWidget.id;
      const duplicateName = getDuplicateFileName(state, {
        fileId: customWidgetId
      });
      dispatch(request(layoutId, widgetId, duplicateName));

      if (customWidgetId) {
        customWidgetBuffersClient.makeLocalCopy(customWidgetId, duplicateName).then(customWidgetData => {
          dispatch(receive(layoutId, widgetId, customWidgetData));
          const widget = new CustomWidget(customWidgetData);
          const newWidget = widget.generateNewLayoutWidgetObject();
          newWidget.params = Object.assign({}, newWidget.params, {}, pick(targetWidget.params, Widget.DEFAULT_CSS_FIELDS));
          dispatch({
            type: LAYOUT_WIDGET_SWAP,
            layoutId,
            widgetId,
            newWidget
          });
          dispatch(revealInFinder(customWidgetData.id));
          dispatch(toggleRenameAsset(customWidgetData.id));
        }).catch(_error => {
          dispatch(error(layoutId, widgetId, _error));
        });
        return;
      }

      dispatch(error(layoutId, widgetId, new ReferenceError('Unable to find global module')));
      return;
    }

    dispatch(error(layoutId, widgetId, new Error('Invalid request to make local copy of global module')));
  };
}

makeLocalCopyOfCmv2GlobalWidget.request = (layoutId, widgetId, duplicateName) => {
  return {
    type: LAYOUT_WIDGET_MAKE_LOCAL_COPY_CMV2_GLOBAL_WIDGET,
    layoutId,
    widgetId,
    duplicateName
  };
};

makeLocalCopyOfCmv2GlobalWidget.receive = (layoutId, widgetId, customWidget) => {
  return {
    type: LAYOUT_WIDGET_MAKE_LOCAL_COPY_CMV2_GLOBAL_WIDGET,
    layoutId,
    widgetId,
    customWidget
  };
};

makeLocalCopyOfCmv2GlobalWidget.error = (layoutId, widgetId, error) => {
  return {
    type: LAYOUT_WIDGET_MAKE_LOCAL_COPY_CMV2_GLOBAL_WIDGET,
    layoutId,
    widgetId,
    error
  };
};
/**
 * Make local copy of global group.  That is, create a cell from it.
 *
 * @param {String|Number} layoutId  Id of layout with widget.
 * @param {String} widgetId         Id of widget to make local copy of.
 */


export function makeLocalCopyOfGlobalGroup(layoutId, widgetId) {
  const {
    request,
    receive,
    error
  } = makeLocalCopyOfGlobalGroup;
  return (dispatch, getState) => {
    const state = getState(); // Get Widget

    const widget = getWidget(state, {
      layoutId,
      widgetId
    });

    if (!(widget && widget.type === 'global_group')) {
      return;
    }

    const globalGroupsDataByPath = getGlobalGroupsDataByPath(state);
    const {
      generated_template_path: generatedTemplatePath,
      path
    } = widget.params; // Get Global Group ID

    const {
      id
    } = globalGroupsDataByPath[generatedTemplatePath || path] || {};

    if (!id) {
      const referenceError = new ReferenceError('Unable to find global group to copy.');
      referenceError.name = LOCAL_COPY_OF_GLOBAL_GROUP_MISING_ID;
      dispatch(error(layoutId, widgetId, referenceError));
      return;
    } // Fetch Global Group Layout


    dispatch(request(layoutId, widgetId));
    layoutsClient.fetch(id).then(globalGroup => {
      dispatch(receive(layoutId, widgetId, globalGroup));
    }).catch(err => dispatch(error(layoutId, widgetId, err, {
      label: widget.label
    })));
  };
}

makeLocalCopyOfGlobalGroup.request = (layoutId, widgetId) => {
  return {
    type: LAYOUT_WIDGET_MAKE_LOCAL_COPY_GLOBAL_GROUP,
    layoutId,
    widgetId
  };
};

makeLocalCopyOfGlobalGroup.receive = (layoutId, widgetId, globalGroup) => {
  return {
    type: LAYOUT_WIDGET_MAKE_LOCAL_COPY_GLOBAL_GROUP,
    layoutId,
    widgetId,
    globalGroup
  };
};

makeLocalCopyOfGlobalGroup.error = (layoutId, widgetId, error, meta) => {
  return {
    type: LAYOUT_WIDGET_MAKE_LOCAL_COPY_GLOBAL_GROUP,
    layoutId,
    widgetId,
    error,
    meta
  };
};
/*
 * Add or edit a widget's smart rules
 *
 * @param {String|Number} layoutId    Id of layout with widget.
 * @param {String}        widgetId    Id of widget to edit.
 * @param {Object}        smartObject Object returned from smart rules manager
 */


export function editWidgetSmartRules(layoutId, widgetId, _smartObject) {
  return (dispatch, getState) => {
    const state = getState();
    const widgetType = getWidgetType(state, {
      layoutId,
      widgetId
    });
    let smartObject = _smartObject;

    if (isLegacyFormType(widgetType)) {
      const {
        content: defaultParams,
        smart_objects: rules
      } = smartObject;
      smartObject = Object.assign({}, smartObject, {
        content: defaultParams && getSyncedFormWidgetParams(defaultParams),
        smart_objects: rules.map(edits => getSyncedFormWidgetParams(edits))
      });
    }

    dispatch({
      type: LAYOUT_WIDGET_EDIT_SMART_RULES,
      layoutId,
      widgetId,
      smartObject
    });
  };
}
/**
 * Remove a widget's smart rules
 *
 * @param {String|Number} layoutId Id of layout with widget.
 * @param {String}        widgetId Id of widget to remove smart rules from.
 */

export function removeWidgetSmartRules(layoutId, widgetId) {
  return {
    type: LAYOUT_WIDGET_REMOVE_SMART_RULES,
    layoutId,
    widgetId
  };
}
/**
 * Add an individual smart rule for a specified widget.
 *
 * @param {String|Number} layoutId Id of layout with widget.
 * @param {String} widgetId        Id of widget to edit.
 * @param {Object} smartObject     Object returned from smart rules manager
 */

export function addWidgetSmartRule(layoutId, widgetId, smartObject) {
  return {
    type: LAYOUT_WIDGET_ADD_SMART_RULE,
    layoutId,
    widgetId,
    smartObject
  };
}
/**
 * Edit an individual smart rule for a specified widget.
 *
 * @param {String|Number} layoutId Id of layout with widget.
 * @param {String} widgetId        Id of widget to edit.
 * @param {Number} smartRuleId     Id of smart rule to edit.
 * @param {Object} edits           Edits to be made to smart rule.
 */

export function editWidgetSmartRule(layoutId, widgetId, smartRuleId, _edits = {}) {
  return (dispatch, getState) => {
    const state = getState();
    const widgetType = getWidgetType(state, {
      layoutId,
      widgetId
    });
    let edits = _edits;

    if (isLegacyFormType(widgetType)) {
      edits = getSyncedFormWidgetParams(edits);
    }

    dispatch({
      type: LAYOUT_WIDGET_EDIT_SMART_RULE,
      layoutId,
      widgetId,
      smartRuleId,
      edits
    });
  };
}
/**
 * Remove an individual smart rule for a specified widget.
 *
 * @param {String|Number} layoutId Id of layout with widget.
 * @param {String} widgetId        Id of widget to edit.
 * @param {Number} smartRuleId     Id of smart rule to remove.
 */

export function removeWidgetSmartRule(layoutId, widgetId, smartRuleId) {
  return {
    type: LAYOUT_WIDGET_REMOVE_SMART_RULE,
    layoutId,
    widgetId,
    smartRuleId
  };
}
/**
 * Edit smart rules order for a specified widget.
 *
 * @param {String|Number} layoutId      Id of layout with widget.
 * @param {String} widgetId             Id of widget to edit.
 * @param {Array.<Object>} smartObjects Array of smart rule objects.
 */

export function editWidgetSmartRulesOrder(layoutId, widgetId, smartObjects) {
  return {
    type: LAYOUT_WIDGET_EDIT_SMART_RULES_ORDER,
    layoutId,
    widgetId,
    smartObjects
  };
}
/**
 * Updates smart rules for a specified widget.
 *
 * @param {String|Number} layoutId
 * @param {String} widgetId
 * @param {Array.<Object>} smartObjects
 * @param {Object} smartMetaData
 */

export function updateSmartRules(layoutId, widgetId, smartObjects, smartMetaData) {
  return {
    type: LAYOUT_WIDGET_UPDATE_SMART_RULES,
    layoutId,
    widgetId,
    smartObjects,
    smartMetaData
  };
}
/**
 * Edit a custom widget
 *
 * @param {String|Number} layoutId  Id of layout with custom widget.
 * @param {String} widgetId         Id of widget representing custom widget.
 */

export function editCustomWidget(layoutId, widgetId) {
  return (dispatch, getState) => {
    const state = getState();
    const targetWidget = getWidget(state, {
      layoutId,
      widgetId
    });

    if (targetWidget) {
      const {
        path: customWidgetPath,
        custom_widget_id: customWidgetId,
        widget_name: customWidgetName
      } = targetWidget.params;
      let customWidget;

      if (customWidgetPath || customWidgetId) {
        const customWidgetsData = getCustomWidgetsById(state);
        customWidget = findCustomWidget(targetWidget, customWidgetsData);
      } else if (customWidgetName) {
        customWidget = getCustomWidgetsByName(state)[customWidgetName];
      }

      if (customWidget) {
        dispatch({
          type: LAYOUT_WIDGET_EDIT_CUSTOM_WIDGET,
          customWidget
        });
        return;
      }
    }

    dispatch({
      type: LAYOUT_WIDGET_EDIT_CUSTOM_WIDGET,
      layoutId,
      widgetId,
      error: new ReferenceError('Unable to find custom module to edit')
    });
  };
}
/*
 * Add or edit a widget's smart rules
 *
 * @param {String|Number} layoutId Id of layout with widget.
 * @param {String}        widgetId Id of widget to edit.
 * @param {Object}        data - Details for global group creation, including rows with contained widgets, name (name of global group, will become the filename), and folderId (destination folder)
 */

export function makeGroupGlobalModernIDE(layoutId, widgetId, data) {
  const {
    request,
    receive,
    swap,
    error
  } = makeGroupGlobalModernIDE;
  return (dispatch, getState) => {
    const state = getState();
    const targetWidget = getWidget(state, {
      layoutId,
      widgetId
    });

    if (targetWidget) {
      const {
        widgets,
        rows
      } = getLayoutData(state, {
        layoutId
      });
      const normalizedWidgetLayoutData = createLayoutTreeFromNormalizedData(widgets, rows, widgetId);
      const requestData = {
        label: data.name,
        folderId: data.folderId,
        templateType: 18,
        layoutData: {
          type: 'cell',
          x: 0,
          w: 12,
          rows: normalizedWidgetLayoutData.rows
        }
      };
      dispatch(request(layoutId, widgetId, requestData));
      layoutsClient.create(requestData).then(globalGroup => {
        dispatch(receive(layoutId, widgetId, globalGroup));
        const widget = new GlobalGroup(globalGroup);
        const newWidget = widget.generateNewLayoutWidgetObject();
        newWidget.params = Object.assign({}, newWidget.params, {}, pick(targetWidget.params, Widget.DEFAULT_CSS_FIELDS));
        dispatch(swap(layoutId, widgetId, newWidget));
      }).catch(__error => {
        dispatch(error(layoutId, widgetId, __error, data.name));
      });
      return;
    }

    dispatch(error(layoutId, widgetId, new ReferenceError('Unable to find group to make global')));
  };
}

makeGroupGlobalModernIDE.request = (layoutId, widgetId, data) => {
  return {
    type: LAYOUT_CELL_MAKE_GLOBAL,
    layoutId,
    widgetId,
    data
  };
};

makeGroupGlobalModernIDE.receive = (layoutId, widgetId, globalGroup) => {
  return {
    type: LAYOUT_CELL_MAKE_GLOBAL,
    layoutId,
    widgetId,
    globalGroup
  };
};

makeGroupGlobalModernIDE.swap = (layoutId, widgetId, newWidget) => {
  return {
    type: LAYOUT_WIDGET_SWAP,
    layoutId,
    widgetId,
    newWidget
  };
};

makeGroupGlobalModernIDE.error = (layoutId, widgetId, error, groupName) => {
  return {
    type: LAYOUT_CELL_MAKE_GLOBAL,
    layoutId,
    widgetId,
    error,
    groupName
  };
};
/**
 * Make global group
 *
 * @param {String|Number} layoutId  Id of layout with group.
 * @param {String} widgetId         Id of group to make global.
 * @param {Object} data             Object with location data for global group.
 */


export function makeGroupGlobal(layoutId, widgetId, data) {
  const {
    request,
    receive,
    swap,
    error
  } = makeGroupGlobal;
  return (dispatch, getState) => {
    const state = getState();
    const targetWidget = getWidget(state, {
      layoutId,
      widgetId
    });

    if (targetWidget) {
      const {
        widgets,
        rows
      } = getLayoutData(state, {
        layoutId
      });
      const normalizedWidgetLayoutData = createLayoutTreeFromNormalizedData(widgets, rows, widgetId);
      const requestData = {
        label: data.path.split('/').slice(1).concat(data.name).join('/'),
        folder: 'generated_global_groups',
        categoryId: 1,
        templateType: 18,
        layoutData: {
          type: 'cell',
          x: 0,
          w: 12,
          rows: normalizedWidgetLayoutData.rows
        }
      };
      dispatch(request(layoutId, widgetId, requestData));
      layoutsClient.create(requestData).then(globalGroup => {
        dispatch(receive(layoutId, widgetId, globalGroup));
        const widget = new GlobalGroup(globalGroup);
        const newWidget = widget.generateNewLayoutWidgetObject();
        newWidget.params = Object.assign({}, newWidget.params, {}, pick(targetWidget.params, Widget.DEFAULT_CSS_FIELDS));
        dispatch(swap(layoutId, widgetId, newWidget));
      }).catch(__error => {
        dispatch(error(layoutId, widgetId, __error));
      });
      return;
    }

    dispatch(error(layoutId, widgetId, new ReferenceError('Unable to find group to make global')));
  };
}

makeGroupGlobal.request = (layoutId, widgetId, data) => {
  return {
    type: LAYOUT_CELL_MAKE_GLOBAL,
    layoutId,
    widgetId,
    data
  };
};

makeGroupGlobal.receive = (layoutId, widgetId, globalGroup) => {
  return {
    type: LAYOUT_CELL_MAKE_GLOBAL,
    layoutId,
    widgetId,
    globalGroup
  };
};

makeGroupGlobal.swap = (layoutId, widgetId, newWidget) => {
  return {
    type: LAYOUT_WIDGET_SWAP,
    layoutId,
    widgetId,
    newWidget
  };
};

makeGroupGlobal.error = (layoutId, widgetId, error) => {
  return {
    type: LAYOUT_CELL_MAKE_GLOBAL,
    layoutId,
    widgetId,
    error
  };
};
/**
 * Edit global group
 *
 * @param {String|Number} layoutId  Id of layout with group.
 * @param {String} widgetId         Id of widget respresenting global group.
 */


export function editGlobalGroup(layoutId, widgetId) {
  return (dispatch, getState) => {
    const state = getState();
    const targetWidget = getWidget(state, {
      layoutId,
      widgetId
    });

    if (targetWidget) {
      const {
        generated_template_path: generatedTemplatePath,
        path
      } = targetWidget.params;
      const globalGroupsDataByPath = getGlobalGroupsDataByPath(state);
      const globalGroup = globalGroupsDataByPath[generatedTemplatePath || path];
      const globalGroupId = globalGroup && globalGroup.id;

      if (globalGroupId) {
        dispatch({
          type: LAYOUT_CELL_EDIT_GLOBAL_GROUP,
          globalGroup
        });
        return;
      }
    }

    dispatch({
      type: LAYOUT_CELL_EDIT_GLOBAL_GROUP,
      layoutId,
      widgetId,
      error: new ReferenceError('Unable to find global group to edit')
    });
  };
}
/**
 * Discard buffered changes for layout.
 *
 * @param {String|Number} layoutId
 */

export function discardBuffer(layoutId) {
  const {
    request,
    receive,
    error
  } = discardBuffer;
  return dispatch => {
    dispatch(request(layoutId));
    return layoutsClient.discardBuffer(layoutId).then(layout => {
      dispatch(receive(layoutId, layout));
    }).catch(err => {
      dispatch(error(layoutId, err));
    });
  };
}

discardBuffer.request = function request(layoutId) {
  return {
    type: LAYOUT_DISCARD_BUFFER,
    layoutId
  };
};

discardBuffer.receive = function receive(layoutId, layout) {
  return {
    type: LAYOUT_DISCARD_BUFFER,
    layoutId,
    layout
  };
};

discardBuffer.error = function error(layoutId, err) {
  return {
    type: LAYOUT_DISCARD_BUFFER,
    layoutId,
    error: err
  };
};
/**
 * Revert a layout.
 *
 * @param {String|Number} layoutId
 */


export function revertLayout(revision) {
  const layoutId = revision.object.id;

  if (revision.id === -1) {
    return discardBuffer(layoutId);
  }

  const {
    receive,
    error
  } = revertLayout;
  return dispatch => {
    return revisionsClient.revertLayout(layoutId, revision.id, true).then(layout => {
      dispatch(receive(layoutId, layout));
    }).catch(_error => {
      dispatch(error(layoutId, _error));
    });
  };
}

revertLayout.receive = (layoutId, layout) => {
  return {
    type: LAYOUT_REVERT,
    layoutId,
    layout
  };
};

revertLayout.error = (layoutId, error) => {
  return {
    type: LAYOUT_REVERT,
    layoutId,
    error
  };
};
/**
 * Restore disconnected layout widgets.
 * @param {String|Number} layoutId  Id of layout containing disconnected widget.
 * @param {String} widgetId         Id of disconnected widget.
 */


export function restoreWidgetParent(layoutId, widgetId) {
  const {
    request,
    receive,
    error
  } = restoreWidgetParent;
  return (dispatch, getState) => {
    const state = getState();
    const targetWidget = getWidget(state, {
      layoutId,
      widgetId
    });
    const {
      label: widgetLabel,
      type: widgetType
    } = targetWidget;
    let restorePromise;

    switch (widgetType) {
      case 'global_widget':
        {
          const {
            name: globalWidgetName
          } = targetWidget;
          restorePromise = globalWidgetsClient.fetchQuery({
            name: globalWidgetName,
            includeDeleted: true,
            limit: 1
          }).then(response => {
            if (!(response.objects && response.objects.length)) {
              const referenceError = new ReferenceError(`Could not find global widget with name '${globalWidgetName}'.`);
              referenceError.name = RESTORE_MISSING_GLOBAL_WIDGET_NAME;
              dispatch(error(layoutId, widgetId, referenceError, {
                globalWidgetName
              }));
              throw referenceError;
            }

            const {
              id
            } = response.objects[0];
            return globalWidgetsClient.restoreDeleted(id);
          });
          break;
        }

      case 'global_group':
        {
          const {
            path: globalGroupPath
          } = targetWidget.params;
          restorePromise = layoutsClient.fetchQuery({
            generatedTemplatePath: globalGroupPath,
            includeDeleted: true,
            limit: 1
          }).then(response => {
            if (!(response.objects && response.objects.length)) {
              const referenceError = new ReferenceError(`Could not find global group with path '${globalGroupPath}'.`);
              referenceError.name = RESTORE_MISSING_GLOBAL_GROUP_PATH;
              dispatch(error(layoutId, widgetId, referenceError, {
                globalGroupPath
              }));
              throw referenceError;
            }

            const {
              id
            } = response.objects[0];
            return layoutsClient.restoreDeleted(id);
          });
          break;
        }

      case 'custom_widget':
        {
          const {
            path,
            widget_name: customWidgetName
          } = targetWidget.params;
          const customWidgetsData = getCustomWidgetsById(state);
          const customWidget = findCustomWidget(targetWidget, customWidgetsData);
          const customWidgetId = customWidget && customWidget.id; // TODO: Delete this block when ungated to all.

          if (customWidgetId && !getIsModuleIdMigrationUngated(state)) {
            restorePromise = customWidgetsClient.restoreDeleted(customWidgetId);
            break;
          }

          if (customWidget && customWidget.moduleId) {
            restorePromise = customWidgetsClient.byModuleId.restoreDeleted(customWidget.moduleId);
          } else {
            const query = {
              includeDeleted: true,
              limit: 1
            };

            if (path) {
              query.path = path;
            } else {
              query.name = customWidgetName;
            }

            restorePromise = customWidgetsClient.fetchQuery(query).then(response => {
              if (!(response.objects && response.objects.length)) {
                const name = path || customWidgetName;
                const referenceError = new ReferenceError(`Could not find custom widget with name '${name}'.`);
                referenceError.name = RESTORE_MISSING_CUSTOM_WIDGET_NAME;
                dispatch(error(layoutId, widgetId, referenceError, {
                  customWidgetName: name
                }));
                throw referenceError;
              }

              const mod = response.objects[0];
              return customWidgetsClient.byModuleId.restoreDeleted(mod.moduleId);
            });
          }

          break;
        }

      default:
        return;
    }

    dispatch(request(layoutId, widgetId));
    restorePromise.then(response => {
      dispatch(receive(layoutId, widgetId, widgetType, response));
    }).catch(__error => {
      if (!(__error instanceof ReferenceError)) {
        dispatch(error(layoutId, widgetId, __error, {
          widgetLabel
        }));
      }
    });
  };
}

restoreWidgetParent.request = (layoutId, widgetId) => {
  return {
    type: LAYOUT_WIDGET_RESTORE_PARENT,
    layoutId,
    widgetId
  };
};

restoreWidgetParent.receive = (layoutId, widgetId, widgetType, parent) => {
  return {
    type: LAYOUT_WIDGET_RESTORE_PARENT,
    layoutId,
    widgetId,
    widgetType,
    parent
  };
};

restoreWidgetParent.error = (layoutId, widgetId, error, meta = {}) => {
  return {
    type: LAYOUT_WIDGET_RESTORE_PARENT,
    layoutId,
    widgetId,
    error,
    meta
  };
};
/**
 * Updates layout model folder when file is moved
 * @param {Number} fileId
 * @param {Number} file
 */


export function updateLayoutFolder(layoutId, file) {
  return {
    type: LAYOUT_UPDATE_FOLDER,
    layoutId,
    file
  };
}
/*****************************
 *
 * Layout Localization Actions
 *
 *****************************/

export function localizationOptIn(layoutId, langCode) {
  return {
    type: LAYOUT_LOCALIZATION_OPT_IN,
    layoutId,
    langCode
  };
}
export function localizationAddLanguage(layoutId, langCode) {
  return {
    type: LAYOUT_LOCALIZATION_ADD_LANGUAGE,
    layoutId,
    langCode
  };
}
export function localizationChangeMasterLanguage(layoutId, langCode) {
  return {
    type: LAYOUT_LOCALIZATION_CHANGE_MASTER_LANGUAGE,
    layoutId,
    langCode
  };
}
export function localizationDeleteLanguage(layoutId, langCode) {
  return {
    type: LAYOUT_LOCALIZATION_DELETE_LANGUAGE,
    layoutId,
    langCode
  };
}